import './MainTabBar.less';

import clsx from 'clsx';
import { List, Map } from 'immutable';
import React, { memo, useEffect, useRef } from 'react';
import { appDb, dispatch, useReframe } from 'nike-re-framejs';

import { isLoggedIn } from '@/account';
import { CHAT, fireGAEvent } from '@/analytics';
import { quickSubmitBet } from '@/betslip/commands';
import { CURRENCY_MAP } from '@/common/client';
import { CURRENT_PAGE } from 'nike-ui/src/common/routing';
import i18n from '@i18n';
import NikeLogoIcon from '@/icons/NikeLogoIcon';
import TicketIcon from '@/icons/TicketIcon';
import scrollTop from '@/root/scrollTop';
import { getRouteByName, navigate, updateURLSearchParams } from '@/routing';
import rHistory from '@/routing/history';
import { hasQueryParam } from '@/routing/QueryParamRoute';
import { getCasinouRoutes, getCommunityRoutes, getOfferRouter, getTicketRoutes } from '@/routing/route-groups';
import {
    COMMUNITY_LOBBY,
    CASINO_GAME_DETAIL,
    HOME,
    MARKETING_ARTICLE,
    MENU,
    NIKE1_DETAIL,
    NIKE1_MATCH,
    TICKET_DETAIL,
    TICKET_DETAIL_ALIAS,
    TURBO_LIVE_MATCH_DETAIL
} from '@/routing/routes';
import { getKey, setKey } from '@/services/localStorage';
import { getObject, setObject } from '@/services/sessionStorage';
import PopupNg from '@/ui/PopupNg';
import SportsbookAZIcon from 'nike-ui/src/icons/svg/sportsbook-az-icon-sv.svg';
import MainTabBarCasinoIcon from 'nike-ui/src/icons/svg/main-tab-bar-casino-icon-sv.svg';
import CommunityIcon from 'nike-ui/src/icons/svg/community-icon-sv.svg';
import { motionElementSlideVariant } from 'nike-ui/src/common/util';
import { AnimatePresence, motion } from 'framer-motion';
import { clearCashinClosingMode } from 'nike-ui/src/components/ticketus/handlers';
import { CollapsedBetslipHeader, CollapsedBetslipMini } from '@/betslip/views/BetslipHeader';
import { isMenuRoute, isRegistrationRoute } from '@/routing/routeutils';
import { isProfileBookingLandscapeEnabled } from 'nike-ui/src/profiles';

const ROUTES_WITHOUT_TABBAR = [
    NIKE1_DETAIL,
    NIKE1_MATCH,
    TURBO_LIVE_MATCH_DETAIL,
    CASINO_GAME_DETAIL,
    TICKET_DETAIL,
    TICKET_DETAIL_ALIAS,
    MARKETING_ARTICLE
];

function CasinoIconWithBadge() {
    const casinoVisited = !!getKey('casinoVisited');

    return (
        <>
            {casinoVisited ? null : <div className="casino-badge item-badge" data-badge=" " />}
            <MainTabBarCasinoIcon />
        </>
    );
}

function CommunityIconWithBadge() {
    const betbookVisited = !!getKey('betbookVisited');

    return (
        <>
            {betbookVisited ? null : <div className="casino-badge item-badge" data-badge=" " />}
            <CommunityIcon width={32} height={24} />
        </>
    );
}

const Tab = memo(function Tab({ active, icon: Icon, text, onClick, atid }) {
    return (
        <div className={clsx('main-tab', 'ripple', { active })} onClick={onClick} data-atid={atid}>
            <div className="flex flex-col full-height">
                <div className="main-tab-content-icon flex-center">
                    <Icon />
                </div>

                <p>{text}</p>
            </div>
        </div>
    );
});

export function interceptPageUpdate(route) {
    if (getOfferRouter().includes(route)) {
        setObject('nike.lastpage.ponuka', route.name);
        return;
    }
    if (getCasinouRoutes().includes(route)) {
        setObject('nike.lastpage.casino', route.name);
    }
    if (getCommunityRoutes().includes(route)) {
        setObject('nike.lastpage.community', route.name);
    }
}

function closeTicket() {
    if (hasQueryParam(rHistory.location, 'tiket')) {
        navigate(CURRENT_PAGE);
        return true;
    }
    return false;
}

function getLastSessionRoutePonuka() {
    const route = getRouteByName(getObject('nike.lastpage.ponuka'));
    return getOfferRouter().includes(route) ? route : getOfferRouter()[0];
}

function getLastSessionRouteCasino() {
    const route = getRouteByName(getObject('nike.lastpage.casino'));
    return getCasinouRoutes().includes(route) ? route : getCasinouRoutes()[0];
}

function currentRoute() {
    return appDb.peekValue().getIn(['route']);
}

function tryToCloseAnyMenuPopup() {
    if (
        !appDb
            .peekValue()
            .getIn(['gui', 'popup/ng'], List())
            .isEmpty()
    ) {
        PopupNg.dispatcher.closeAll();
        return true;
    }
    dispatch(clearCashinClosingMode);
    return false;
}

function onPonukaClick() {
    if (tryToCloseAnyMenuPopup()) {
        return;
    }
    const route = getLastSessionRoutePonuka();
    if (route === currentRoute()) {
        closeTicket() || scrollTop();
    } else {
        navigate(route);
    }
}

function onHomeClick() {
    if (tryToCloseAnyMenuPopup()) {
        return;
    }
    if (HOME === currentRoute()) {
        closeTicket() || scrollTop();
    } else {
        navigate(HOME);
    }
}

function onTicketsClick() {
    if (tryToCloseAnyMenuPopup()) {
        return;
    }

    if (getTicketRoutes()[0] === currentRoute()) {
        closeTicket() || scrollTop();
    } else {
        navigate(getTicketRoutes()[0]);
    }
}

function onCasinoClick() {
    setKey('casinoVisited', true);
    if (tryToCloseAnyMenuPopup()) {
        return;
    }
    const route = getLastSessionRouteCasino();
    if (route === currentRoute()) {
        closeTicket() || scrollTop();
    } else {
        navigate(route);
    }
}

function onCommunityClick() {
    setKey('betbookVisited', true);
    if (tryToCloseAnyMenuPopup()) {
        return;
    }
    const route = COMMUNITY_LOBBY;
    if (route === currentRoute()) {
        closeTicket() || scrollTop();
    } else {
        navigate(route);
    }
    fireGAEvent(CHAT, 'otvorenie_chatu');
}

const MotionDiv = ({ children }) => (
    <AnimatePresence>
        <motion.div
            className="flex full-width"
            variants={motionElementSlideVariant}
            initial="initial"
            animate="enter"
            exit="exit"
            key="menu"
        >
            {children}
        </motion.div>
    </AnimatePresence>
);

function MainNavBar({ route, children }) {
    const menu = route === MENU;
    return (
        <div className={clsx('main-tab-bar', { menu })}>
            <MotionDiv>
                <Tab
                    text={i18n('menu.home')}
                    active={HOME === route}
                    icon={NikeLogoIcon}
                    onClick={onHomeClick}
                    atid="tabBar-goTo-home"
                />
                <Tab
                    text={i18n('menu.sport')}
                    active={getOfferRouter().includes(route)}
                    icon={SportsbookAZIcon}
                    onClick={onPonukaClick}
                    atid="tabBar-goTo-offer"
                />
                <Tab
                    text={i18n('menu.tikety')} // HU: Szelvények
                    active={getTicketRoutes().includes(route)}
                    icon={TicketIcon}
                    onClick={onTicketsClick}
                    atid="tabBar-goTo-tickets"
                />
                <Tab
                    text={i18n('menu.chat')}
                    active={getCommunityRoutes().includes(route)}
                    icon={CommunityIconWithBadge}
                    onClick={onCommunityClick}
                    atid="tabBar-goTo-chat"
                />
                <Tab
                    text={i18n('menu.casino')}
                    active={getCasinouRoutes().includes(route)}
                    icon={CasinoIconWithBadge}
                    onClick={onCasinoClick}
                    atid="tabBar-goTo-casino"
                />
                {children}
            </MotionDiv>
        </div>
    );
}

function QuickBetBar({ children }) {
    const derefSub = useReframe();
    const betslip = derefSub(['server/betslip']);
    return (
        <div className={clsx('main-tab-bar bet-container')}>
            <MotionDiv>
                {lastThreeBets(betslip)
                    .sort((a, b) => (+a.get('stake') > +b.get('stake') ? 1 : -1))
                    .map((bet, index) => (
                        <QuickBetButton key={index} index={index} stake={bet.get('stake')} win={bet.get('win')} />
                    ))}
                {children}
            </MotionDiv>
        </div>
    );
}

export function lastThreeBets(betslip) {
    const firstStake = betslip.getIn(['totals', 'stake', 'money']);
    const firstWin = betslip.getIn(['totals', 'win', 'money']);
    const quickBets = betslip.getIn(['quickBets'], List());

    const mergedAndSortedQuickBets = Object.fromEntries(
        quickBets
            .unshift(Map({ stake: firstStake, win: firstWin }))
            .sortBy(quickbet => quickbet.get('stake'))
            .map(quickbet => [quickbet.get('stake'), quickbet])
    );

    return Object.values(mergedAndSortedQuickBets).slice(0, 3);
}

function QuickBetButton({ index, stake, win }) {
    const stakeMoney = stake + ' ' + CURRENCY_MAP.get('EUR');
    const winMoney = win + ' ' + CURRENCY_MAP.get('EUR');
    const sliderRef = useRef();
    useEffect(() => {
        sliderRef.current.classList.add('slided');
    });
    return (
        <div className="bet-button content-slider" ref={sliderRef} onClick={() => submitQuickBet(stake, index)}>
            <div className="stake">{stakeMoney}</div>
            <div className="win">{winMoney}</div>
        </div>
    );
}

function submitQuickBet(stake, key) {
    fireGAEvent('Editor tiketu', `Rychle podanie ${stake}`);

    if (isLoggedIn()) {
        dispatch(quickSubmitBet({ amount: stake }));
    } else {
        updateURLSearchParams({ prihlasenie: null });
    }
}

//neskor tam zjavne bude aj tlacidlo pre podanie na betslipe, takze to bude MainBar... s obcerstvenim a vsetkym
function MainTabOrQuickBetBar({ route, children }) {
    const { derefSub } = useReframe();

    const quickbetViable = derefSub(['betslip/quickbet/visible']);
    return quickbetViable ? <QuickBetBar>{children}</QuickBetBar> : <MainNavBar route={route}>{children}</MainNavBar>;
}

const MainTabBar = ({ route, landscape }) => {
    const hideNavBar = ROUTES_WITHOUT_TABBAR.includes(route) && isProfileBookingLandscapeEnabled();
    const showHeader = !isMenuRoute(route) && !isRegistrationRoute(route) && (!landscape || hideNavBar);
    return (
        <>
            {!hideNavBar &&
                (landscape ? (
                    <MainTabOrQuickBetBar route={route}>
                        <CollapsedBetslipMini />
                    </MainTabOrQuickBetBar>
                ) : (
                    <MainTabOrQuickBetBar route={route} />
                ))}
            {showHeader && <CollapsedBetslipHeader otherClass={hideNavBar ? 'betslip-header-detail' : null} />}
        </>
    );
};

export default MainTabBar;
